import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Select } from 'antd';
import { consumeAuthRedirectMessage } from '../../services/sessionMigration';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import {
  getPathForClientShell,
  getPostAuthPath,
  setClientShell,
  type ClientShell,
} from '../../constants/clientShell';
import type { LoginMethod } from '../../stores/authStore';
import { useI18n } from '../../i18n/useI18n';
import { SITE_LANGUAGE_OPTIONS } from '../../i18n/types';
import './AuthStyles.css';

type AuthView = 'login' | 'register' | 'forgot-password';

interface AuthPageProps {
  initialLoginMethod?: LoginMethod;
  adminRedirect?: boolean;
}

export const AuthPage: React.FC<AuthPageProps> = ({
  initialLoginMethod = 'username',
  adminRedirect = false,
}) => {
  const [view, setView] = useState<AuthView>('login');
  const navigate = useNavigate();
  const { language, changeLanguage, t } = useI18n();

  useEffect(() => {
    const redirectMessage = consumeAuthRedirectMessage();
    if (redirectMessage) {
      message.info(t('auth.sessionExpired'));
    }
  }, [t]);

  const handleLoginSuccess = useCallback(
    (method?: LoginMethod, shell?: ClientShell) => {
      if (method === 'admin' || adminRedirect) {
        navigate('/control/dashboard', { replace: true });
        return;
      }
      if (shell) {
        setClientShell(shell);
        navigate(getPathForClientShell(shell), { replace: true });
        return;
      }
      navigate(getPostAuthPath(), { replace: true });
    },
    [adminRedirect, navigate],
  );

  return (
    <div className="auth-page">
      <div className="auth-language-picker">
        <span className="auth-language-picker__label">{t('auth.language')}</span>
        <Select
          aria-label={t('auth.languageAria')}
          value={language}
          options={SITE_LANGUAGE_OPTIONS}
          onChange={changeLanguage}
          popupMatchSelectWidth={false}
        />
      </div>
      {view === 'login' && (
        <LoginForm
          initialLoginMethod={initialLoginMethod}
          onSuccess={handleLoginSuccess}
          onSwitchToRegister={() => setView('register')}
          onForgotPassword={() => setView('forgot-password')}
        />
      )}
      {view === 'register' && (
        <RegisterForm
          onSuccess={() => setView('login')}
          onSwitchToLogin={() => setView('login')}
        />
      )}
      {view === 'forgot-password' && (
        <ForgotPasswordForm onBackToLogin={() => setView('login')} />
      )}
    </div>
  );
};

export default AuthPage;
